import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Login } from './Login';
import { SignUp } from './SignUp';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { AcceptInvite } from './AcceptInvite';
import { Navigation } from './Navigation';
import { TeamSelection } from './TeamSelection';
import { SquadAnalysis } from './SquadAnalysis';
import { TrainingPlanner } from './TrainingPlanner';
import { TrainingSessions } from './TrainingSessions';
import { BuildFromPdf } from './TrainingSessions/BuildFromPdf';
import { TrainingDrills } from './TrainingDrills';
import { EditDrill } from './EditDrill';
import { YouTubeSearch } from './YouTubeSearch';
import { ReseedDrills } from './Drills/ReseedDrills';
import { SuperUserDashboard } from './SuperUserDashboard';
import { PlayerTraining } from './PlayerTraining';
import { PageLoading } from './PageLoading';
import { PageFooter } from './PageFooter';
import { AuthService } from './AuthService';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { ReferenceDataProvider, useReferenceData } from './ReferenceDataContext';

function RouteGuard() {
  const { currentShareType } = useReferenceData();
  const location = useLocation();

  if (currentShareType === 'Player' && location.pathname !== '/training') {
    return <Navigate to="/training" replace />;
  }
  if (currentShareType !== 'Player' && location.pathname === '/training') {
    return <Navigate to="/team-selection" replace />;
  }
  return null;
}

function DefaultRedirect() {
  const { currentShareType } = useReferenceData();
  const target = currentShareType === 'Player' ? '/training' : '/team-selection';
  return <Navigate to={target} replace />;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [authPage, setAuthPage] = useState<'login' | 'signup' | 'forgotPassword' | 'resetPassword' | 'acceptInvite'>(() => {
    if (window.location.pathname === '/reset-password' && new URLSearchParams(window.location.search).get('token')) {
      return 'resetPassword';
    }
    if (window.location.pathname === '/accept-invite' && new URLSearchParams(window.location.search).get('token')) {
      return 'acceptInvite';
    }
    return 'login';
  });
  const [errors, setErrors] = useState<ErrorMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = () => {
      if (AuthService.isAuthenticated()) {
        setIsAuthenticated(true);
        setCurrentUser(AuthService.getUsername());
        setIsAdmin(AuthService.isAdmin());
        setIsSuperUser(AuthService.isSuperUser());
      } else {
        setIsAuthenticated(false);
        setCurrentUser(null);
        setIsAdmin(false);
        setIsSuperUser(false);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  // Handle login
  const handleLogin = (token: string, username: string, userIsAdmin: boolean, userIsSuperUser: boolean) => {
    AuthService.setAuthData(token, username, undefined, userIsAdmin, undefined, userIsSuperUser);
    setIsAuthenticated(true);
    setCurrentUser(username);
    setIsAdmin(userIsAdmin);
    setIsSuperUser(userIsSuperUser);
  };

  // Handle signup
  const handleSignUp = (token: string, username: string, userIsAdmin: boolean, userIsSuperUser: boolean) => {
    AuthService.setAuthData(token, username, undefined, userIsAdmin, undefined, userIsSuperUser);
    setIsAuthenticated(true);
    setCurrentUser(username);
    setIsAdmin(userIsAdmin);
    setIsSuperUser(userIsSuperUser);
  };

  // Switch between login and signup pages
  const handleSwitchToSignUp = () => {
    setAuthPage('signup');
  };

  const handleSwitchToLogin = () => {
    setAuthPage('login');
    // Clear reset-password/accept-invite URL if navigating back to login
    if (window.location.pathname === '/reset-password' || window.location.pathname === '/accept-invite') {
      window.history.replaceState({}, '', '/');
    }
  };

  const handleSwitchToForgotPassword = () => {
    setAuthPage('forgotPassword');
  };

  // Handle logout
  const handleLogout = useCallback(() => {
    AuthService.logout();
    setIsAuthenticated(false);
    setCurrentUser(null);
    setIsAdmin(false);
    setIsSuperUser(false);
  }, []);

  // Listen for session expiry events from HttpHelpers
  useEffect(() => {
    const handleSessionExpired = () => {
      handleLogout();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [handleLogout]);

  // Helper function to add error messages
  const addError = useCallback((message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    // Check if we already have a similar error message to prevent duplicates
    const isDuplicate = errors.some(error =>
      error.message === message &&
      error.type === type &&
      (Date.now() - error.timestamp.getTime()) < 2000 // Within 2 seconds
    );

    if (isDuplicate) {
      console.log('Preventing duplicate error message:', message);
      return;
    }

    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    // Auto-dismiss after different times based on type
    const dismissTime = type === 'error' ? 10000 : type === 'warning' ? 8000 : 6000;
    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, dismissTime);
  }, [errors]);

  // Helper function to dismiss errors
  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  // Add keyboard shortcuts for debugging
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+Shift+D: Test error notification
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        event.preventDefault();
        addError('Test error message - this simulates a real error', 'error');
      }
      // Ctrl+Shift+R: Reload data manually
      if (event.ctrlKey && event.shiftKey && event.key === 'R') {
        event.preventDefault();
        window.location.reload();
      }
      // Ctrl+Shift+C: Clear all error messages
      if (event.ctrlKey && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        setErrors([]);
        console.log('All error messages cleared');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [addError]);

  // Show login page if not authenticated
  if (!isAuthenticated) {
    if (authPage === 'resetPassword') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token) {
        return <ResetPassword token={token} onSwitchToLogin={handleSwitchToLogin} />;
      }
    }

    if (authPage === 'acceptInvite') {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (token) {
        return <AcceptInvite token={token} onAcceptInvite={handleSignUp} onSwitchToLogin={handleSwitchToLogin} />;
      }
    }

    // Clean up /reset-password and /accept-invite URLs if no valid token is present
    if (window.location.pathname === '/reset-password' || window.location.pathname === '/accept-invite') {
      window.history.replaceState({}, '', '/');
    }

    if (authPage === 'signup') {
      return <SignUp onSignUp={handleSignUp} onSwitchToLogin={handleSwitchToLogin} />;
    } else if (authPage === 'forgotPassword') {
      return <ForgotPassword onSwitchToLogin={handleSwitchToLogin} />;
    } else {
      return <Login onLogin={handleLogin} onSwitchToSignUp={handleSwitchToSignUp} onSwitchToForgotPassword={handleSwitchToForgotPassword} />;
    }
  }

  // Show loading state
  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <ReferenceDataProvider isAuthenticated={isAuthenticated}>
      <BrowserRouter>
        <div className="app-container">
          <ErrorNotification errors={errors} onDismiss={dismissError} />

          <Navigation currentUser={currentUser} isAdmin={isAdmin} isSuperUser={isSuperUser} onLogout={handleLogout} />
          <RouteGuard />

          <Routes>
            <Route
              path="/team-selection"
              element={
                <TeamSelection
                  onError={addError}
                />
              }
            />
            <Route path="/squad-analysis" element={<SquadAnalysis onError={addError} />} />
            <Route path="/training-planner/:sessionId?" element={<TrainingPlanner onError={addError} />} />
            <Route path="/training-sessions" element={<TrainingSessions onError={addError} isAdmin={isAdmin} />} />
            <Route path="/training-sessions/build-from-pdf" element={<BuildFromPdf onError={addError} onSuccess={(msg) => addError(msg, 'info')} />} />
            <Route path="/training-drills" element={<TrainingDrills onError={addError} />} />
            <Route path="/training-drills/edit/:id" element={<EditDrill onError={addError} />} />
            <Route path="/training-drills/youtube-search" element={<YouTubeSearch onError={addError} />} />
            <Route path="/training-drills/reseed" element={<ReseedDrills onError={addError} />} />
            <Route path="/training" element={<PlayerTraining onError={addError} />} />
            <Route path="/admin-dashboard" element={<SuperUserDashboard onError={addError} />} />
            <Route path="/" element={<DefaultRedirect />} />
            <Route path="*" element={<DefaultRedirect />} />
          </Routes>

          <PageFooter />
        </div>
      </BrowserRouter>
    </ReferenceDataProvider>
  );
}
export default App;
